import React, { useEffect, useState } from "react";
import { Coins, ImageIcon, Sparkles, Upload } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalization } from "@/lib/localization";

export function ProductImageGenerateDialog({
  balance,
  defaultDescription = "",
  generating = false,
  onConfirm,
  onOpenChange,
  open,
  product,
}) {
  const { messages } = useLocalization();
  const productName = product?.name || messages.common.generateImageDialogFallbackProduct;
  const creditBalance = balance?.availableCredits || "0";
  const [description, setDescription] = useState(defaultDescription);
  const [mode, setMode] = useState("generate");
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    if (open) {
      setDescription(defaultDescription);
      setMode("generate");
      setImage(null);
      setImageError("");
      return;
    }
    setDescription("");
  }, [defaultDescription, open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[420px] gap-0 overflow-hidden border-emerald-950/10 bg-[#F7F9F5] p-0 shadow-[0_30px_90px_-34px_rgba(6,78,59,0.55)] dark:bg-card">
        <AlertDialogHeader className="relative overflow-hidden bg-[linear-gradient(135deg,#063F35_0%,#0E6753_64%,#138267_100%)] px-5 pb-5 pt-4 text-left text-white">
          <div className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full border border-white/10 bg-white/[0.04]" />
          <div className="pointer-events-none absolute -bottom-16 right-16 h-28 w-28 rounded-full bg-emerald-300/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-emerald-100 shadow-inner">
                <Sparkles className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 space-y-1">
                <AlertDialogTitle className="text-[1.05rem] font-semibold tracking-[-0.025em] text-white">
                  {messages.common.productImageDialogTitle}
                </AlertDialogTitle>
                <AlertDialogDescription className="line-clamp-2 text-[12px] leading-5 text-emerald-50/70">
                  {messages.common.productImageDialogIntro}{" "}
                  <span className="font-medium text-white">{productName}</span>
                </AlertDialogDescription>
              </div>
            </div>
            <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 bg-black/10 px-2.5 py-1 text-[10px] font-medium text-emerald-50">
              <Coins className="h-3 w-3 text-emerald-200" />
              <span>1 / {creditBalance}</span>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 px-5 py-4">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid h-10 w-full grid-cols-2 rounded-xl bg-emerald-950/[0.06] p-1">
              <TabsTrigger
                value="generate"
                className="h-8 gap-1.5 rounded-lg text-[12px] data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm dark:data-[state=active]:bg-background"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {messages.common.aiGenerateImage}
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                className="h-8 gap-1.5 rounded-lg text-[12px] data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm dark:data-[state=active]:bg-background"
              >
                <Upload className="h-3.5 w-3.5" />
                {messages.common.uploadAndClean}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="generate" className="mt-3 focus-visible:ring-0">
              <div className="flex items-center gap-3 rounded-xl border border-emerald-900/10 bg-emerald-50/70 px-3 py-2.5 dark:bg-emerald-950/20">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 shadow-sm dark:bg-background">
                  <ImageIcon className="h-3.5 w-3.5" />
                </div>
                <p className="text-[11px] leading-4 text-emerald-950/65 dark:text-emerald-100/70">
                  {messages.common.generateImageDescriptionHelp}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="upload" className="mt-3 space-y-1.5 focus-visible:ring-0">
              <label
                htmlFor="product-image-upload"
                className="group flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-emerald-800/25 bg-white/60 px-3 py-3 transition-colors hover:border-emerald-700/45 hover:bg-white dark:bg-background/50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-950 text-emerald-50 transition-transform group-hover:-translate-y-0.5">
                  <Upload className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[12px] font-semibold text-foreground">
                    {image?.name || messages.common.chooseProductImage}
                  </span>
                  <span className="mt-0.5 block text-[10px] text-muted-foreground">
                    {messages.common.uploadImageHelp}
                  </span>
                </span>
                <Input
                  id="product-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => {
                    const selectedImage = event.target.files?.[0] || null;
                    if (selectedImage && selectedImage.size > 1024 * 1024) {
                      setImage(null);
                      setImageError(messages.common.uploadImageTooLarge);
                      event.target.value = "";
                      return;
                    }
                    setImage(selectedImage);
                    setImageError("");
                  }}
                  className="sr-only"
                />
              </label>
              {imageError ? <p className="text-[10px] font-medium text-destructive">{imageError}</p> : null}
            </TabsContent>
          </Tabs>

          <div className="space-y-1.5">
            <label htmlFor="product-image-description" className="text-[11px] font-semibold text-foreground/75">
              {messages.common.generateImageDescriptionLabel}
            </label>
            <Input
              id="product-image-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={messages.common.generateImageDescriptionPlaceholder}
              className="h-9 rounded-xl border-emerald-950/10 bg-white/75 text-[12px] shadow-none focus-visible:border-emerald-700/35 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-background"
            />
            {mode === "upload" ? (
              <p className="text-[10px] text-muted-foreground">{messages.common.cleanImageDescriptionHelp}</p>
            ) : null}
          </div>
        </div>

        <AlertDialogFooter className="border-t border-emerald-950/[0.07] bg-white/45 px-5 py-3 dark:bg-background/30">
          <AlertDialogCancel
            disabled={generating}
            className="h-9 rounded-xl border-0 bg-transparent px-4 text-[12px] text-muted-foreground shadow-none hover:bg-emerald-950/[0.05] hover:text-foreground focus-visible:ring-0"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={generating || (mode === "upload" && (!image || Boolean(imageError)))}
            onClick={(event) => {
              event.preventDefault();
              onConfirm?.({ description, image, mode });
            }}
            className="h-9 rounded-xl bg-emerald-700 px-4 text-[12px] text-white shadow-[0_10px_22px_-12px_rgba(4,120,87,0.8)] hover:bg-emerald-800 focus-visible:ring-0"
          >
            {generating
              ? messages.common.processingImage
              : mode === "upload"
                ? messages.common.cleanUploadedImage
                : messages.common.generateImageAction}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
